import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ZiroRadioConfig } from '../../../forms/fields/radio/ziro-radio.component';
import { ModalBodyClass } from '../../../modals/modal-body';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroFieldConfig, ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { DetailedModalComponent } from '../../../modals/detailed-modal/detailed-modal.component';
import {
  MicrosoftDialPlanGroup,
  PrivilegedTenant,
  Role,
  Type,
  UserGroupPermissions,
} from '../../../shared/models/generated/ziroModels';
import { ViewMembersState } from '../../../shared/view-members/view-members.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../forms/fields/select/ziro-select.component';
import { TypeaheadConfig } from '../../../forms/fields/typeahead/typeahead.component';
import { map } from 'rxjs/operators';
import { Microsoft365UserResource } from '../../../helpdesk/shared/resources/microsoft-365-user.resource';
import {
  MicrosoftSecurityGroupsTenantSearchResource,
  TenantMicrosoftSecurityGroup,
} from '../../resources/microsoft-security-groups-tenant-search.resource';

type UserGroupPermissionsWithMutatedName = Omit<UserGroupPermissions, 'name'> & { name: string | ZiroSelectOption };

@Component({
  selector: 'ziro-user-permissions-modal',
  templateUrl: './user-permissions-modal.component.html',
  standalone: false,
})
export class UserPermissionsModalComponent
  extends ModalBodyClass<UserGroupPermissionsWithMutatedName>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;

  private _microsoftSecurityGroupsTenantSearchResource = inject(MicrosoftSecurityGroupsTenantSearchResource);
  private _modalComponent = inject(DetailedModalComponent<any>);
  private _microsoft365UserResource = inject(Microsoft365UserResource);

  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;

  Type = Type;

  isExisting: boolean;
  formConfig: ZiroFormConfig;
  permissionType: Type;
  usersInGroup: string[] = [];
  viewMembersState: ViewMembersState = ViewMembersState.IDLE;
  userGroupPermissions: UserGroupPermissions;
  multiTenantSearchThreshold: number = 4;

  private _tenantIds: string[];
  private _selectUserGroupTenantId: number = null;
  private _allUserGroupPermissions: UserGroupPermissions[];
  private _dialPlanGroups: MicrosoftDialPlanGroup[] = [];
  private _isTenantConfigured: boolean;
  private _microsoftTenants: PrivilegedTenant[] = [];

  private _uniquePermissionValidator = (val: string | ZiroSelectOption): ZiroFormsValidationState => {
    if (
      !!val &&
      !this.isExisting &&
      this._allUserGroupPermissions.some(
        (permission) =>
          permission.name.toLowerCase() ===
          (typeof val === 'string' ? val.trim().toLowerCase() : val.label.trim().toLowerCase()),
      )
    ) {
      return ZiroFormsValidationState.INVALID;
    }
    return ZiroFormsValidationState.VALID;
  };

  ngOnInit(): void {
    this.userGroupPermissions = this._modalComponent.modalViewProperties.userPermission;
    this._allUserGroupPermissions = this._modalComponent.modalViewProperties.allUserPermissions;
    this._dialPlanGroups = this._modalComponent.modalViewProperties.dialPlanGroups;
    this.permissionType = this.userGroupPermissions.type;
    this.isExisting = !!this.userGroupPermissions.role;
    this._isTenantConfigured = this._modalComponent.modalViewProperties.isTenantConfigured;
    this._microsoftTenants = this._modalComponent.modalViewProperties.microsoftTenants;
    this._tenantIds = (this._microsoftTenants ?? []).map((t) => String(t.tenantId));
    this._initFormConfig();
    this.entitySource.next({
      name: this.userGroupPermissions.name,
      type: this.userGroupPermissions.type,
      role: this.userGroupPermissions.role,
      permittedDialPlanGroups: this.userGroupPermissions.permittedDialPlanGroups,
      tenantId: this.userGroupPermissions.tenantId,
    });

    this.ziroFormsUpdate$.subscribe((update) => {
      if (this.permissionType === Type.GROUP && update.new?.name && update.new.name !== update.old?.name) {
        const updatedName = update.new.name;

        this._selectUserGroupTenantId =
          typeof updatedName === 'string' ? this.userGroupPermissions.tenantId : updatedName.value;

        this._getGroupMembers(update.new.name);
      }
    });
  }

  submit(): Observable<void> {
    const updatedName = this.entity.name;

    const requestBody: UserGroupPermissions = {
      name: typeof updatedName === 'string' ? updatedName : updatedName.label,
      type: this.permissionType,
      role: this.entity.role,
      permittedDialPlanGroups: this.entity.role === Role.S8_ADMIN ? [] : this.entity.permittedDialPlanGroups,
      tenantId: this._selectUserGroupTenantId,
    };

    return this._modalComponent.modalViewProperties.submitFn(requestBody);
  }

  private _initFormConfig(): void {
    const options = this._dialPlanGroups.map((group: MicrosoftDialPlanGroup) => {
      return group.name;
    });
    this.formConfig = {
      fields: {
        name:
          this.permissionType === Type.USER
            ? this._buildFieldConfigForUserName()
            : this._buildFieldConfigForGroupName(),
        role: {
          label: 'tkey;admin.system.user_permissions.assign_roles.label',
          dataAutomation: 'user-permissions-modal-role',
          componentConfig: new ZiroRadioConfig<Role>({
            buttons: [
              {
                value: Role.S8_ADMIN,
                label: 'tkey;admin.system.user_permissions.administrator.label',
                helpText: 'tkey;admin.system.user_permissions.administrator.helptext',
              },
              {
                value: Role.S8_HELPDESK,
                label: 'tkey;admin.system.user_permissions.helpdesk.label',
                helpText: 'tkey;admin.system.user_permissions.helpdesk.helptext',
              },
            ],
            inline: false,
            isDisplayVertical: true,
          }),
          required: true,
        },
        permittedDialPlanGroups: {
          label: 'tkey;admin.system.user_permissions.limit_helpdesk.label',
          helpText: 'tkey;admin.system.user_permissions.limit_helpdesk.helptext',
          dataAutomation: 'user-permissions-modal-limit-helpdesk',
          componentConfig: new ZiroSelectConfig({
            options: [...options],
            isMultiSelect: true,
          }),
          disabled: () => this.formData.role === Role.S8_ADMIN || !options.length,
          disabledTooltip: () => {
            return {
              content:
                this.formData.role === Role.S8_ADMIN
                  ? 'tkey;admin.system.user_permissions.limit_helpdesk.tooltip'
                  : 'tkey;admin.system.user_permissions.limit_helpdesk.tooltip.no_groups',
              params: {},
            };
          },
        },
      },
      options: {
        columnClasses: {
          label: 'col-lg-3 text-lg-end',
          input: '',
        },
      },
    };
  }

  private _searchByUserName(searchTerm: string): Observable<string[]> {
    if (!this._isTenantConfigured || this._microsoftTenants?.length >= this.multiTenantSearchThreshold) {
      return of([]);
    }

    return forkJoin(
      this._tenantIds.map((tenantId) => this._microsoft365UserResource.search(tenantId, searchTerm.trim())),
    ).pipe(map((resultSets) => resultSets.flatMap((set) => set.map((r) => r.microsoft365UserRef.id))));
  }

  private _searchByGroupName(searchTerm: string): Observable<ZiroSelectOption[]> {
    if (!this._isTenantConfigured) {
      return of([]);
    }

    return forkJoin(
      this._tenantIds.map((tenantId) =>
        this._microsoftSecurityGroupsTenantSearchResource.search(tenantId, searchTerm, false),
      ),
    ).pipe(
      map((results) =>
        results.flatMap((set) =>
          set.map((r) => ({ label: r.microsoftSecurityGroupRefJson.displayName.trim(), value: r.tenantId })),
        ),
      ),
    );
  }

  private _getGroupMembers(groupName: string | ZiroSelectOption): void {
    if (this._isTenantConfigured && groupName) {
      this.viewMembersState = ViewMembersState.SEARCHING;
      const groupDisplayName = typeof groupName === 'string' ? groupName : groupName.label;

      this._microsoftSecurityGroupsTenantSearchResource
        .get(this._selectUserGroupTenantId.toString(), groupDisplayName)
        .subscribe((data: TenantMicrosoftSecurityGroup) => {
          this.usersInGroup = data.members;
          this._selectUserGroupTenantId = Number(data.tenantId);
          this.viewMembersState = ViewMembersState.IDLE;
        });
    }
  }

  private _buildFieldConfigForUserName(): ZiroFieldConfig<TypeaheadConfig> {
    return {
      label: 'tkey;admin.system.user_permissions.user_name',
      dataAutomation: 'user-permissions-modal-user-name',
      componentConfig: new TypeaheadConfig({
        search: (term) => this._searchByUserName(term?.trim()),
        placeholder: 'tkey;admin.system.user_permissions.user_name.placeholder',
      }),
      required: true,
      disabled: () => this.isExisting,
      disabledTooltip: 'tkey;admin.system.user_permissions.user_name.disabled_tooltip',
      validation: [
        {
          validator: this._uniquePermissionValidator,
          message: 'tkey;admin.system.user_permissions.user_name.unique',
        },
        {
          validator: (val: string) => {
            if (!!val && !/^.+@.+\..+$/.test(val.trim())) {
              return ZiroFormsValidationState.INVALID;
            }
            return ZiroFormsValidationState.VALID;
          },
          message: 'tkey;validators.global.upn.invalid.error',
        },
      ],
    };
  }

  private _buildFieldConfigForGroupName(): ZiroFieldConfig<ZiroSelectConfig> {
    return {
      label: 'tkey;admin.system.user_permissions.group_name',
      dataAutomation: 'user-permissions-modal-group-name',
      componentConfig: new ZiroSelectConfig({
        asyncOptionsFn: (term) => this._searchByGroupName(term),
        minSearchLength: 3,
        placeholder: 'tkey;admin.system.user_permissions.group_name.placeholder',
      }),
      required: true,
      disabled: () => this.isExisting,
      disabledTooltip: 'tkey;admin.system.user_permissions.group_name.disabled_tooltip',
      validation: [
        {
          validator: this._uniquePermissionValidator,
          message: 'tkey;admin.system.user_permissions.group_name.unique',
        },
      ],
    };
  }
}
